if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.socialProofBadge = this.querySelector('[data-social-proof-badge]');
        if (this.socialProofBadge) {
          this.socialProofCloseButton = this.socialProofBadge.querySelector('[data-social-proof-close]');
          this.socialProofCountElement = this.socialProofBadge.querySelector('[data-social-proof-count]');
          this.socialProofDelayTimeout = null;
          this.socialProofHideTimeout = null;
          this.socialProofHasShown = false;
          this.socialProofTTL = 24 * 60 * 60 * 1000;
          this.socialProofStorageKey = this.dataset.productKey
            ? `mgm:socialProof:${this.dataset.productKey}`
            : 'mgm:socialProof:default';
          this.socialProofCount = this.resolveSocialProofCount();
          this.updateSocialProofCount();
          if (this.socialProofCloseButton) {
            this.socialProofCloseButton.addEventListener('click', () => this.hideSocialProof());
          }
          this.observeSocialProof();
        }
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      showSocialProof() {
        if (!this.socialProofBadge || this.socialProofHasShown) return;
        this.socialProofHasShown = true;
        this.socialProofBadge.removeAttribute('hidden');
        this.socialProofBadge.classList.add('is-visible');
        if (this.socialProofHideTimeout) window.clearTimeout(this.socialProofHideTimeout);
        this.socialProofHideTimeout = window.setTimeout(() => {
          this.hideSocialProof();
        }, 5000);
      }

      hideSocialProof() {
        if (!this.socialProofBadge) return;
        this.socialProofBadge.classList.remove('is-visible');
        const badge = this.socialProofBadge;
        window.setTimeout(() => {
          badge.setAttribute('hidden', 'hidden');
        }, 300);
        if (this.socialProofDelayTimeout) {
          window.clearTimeout(this.socialProofDelayTimeout);
          this.socialProofDelayTimeout = null;
        }
        if (this.socialProofHideTimeout) {
          window.clearTimeout(this.socialProofHideTimeout);
          this.socialProofHideTimeout = null;
        }
      }

      observeSocialProof() {
        if (!this.socialProofBadge || this.socialProofHasShown) return;
        if (typeof IntersectionObserver === 'undefined') {
          this.scheduleSocialProof();
          return;
        }
        this.socialProofObserver = new IntersectionObserver(this.onSocialProofIntersection.bind(this), {
          root: null,
          threshold: 0.25,
          rootMargin: '0px 0px -20% 0px',
        });
        this.socialProofObserver.observe(this);
      }

      onSocialProofIntersection(entries) {
        if (this.socialProofHasShown) {
          if (this.socialProofObserver) this.socialProofObserver.disconnect();
          return;
        }
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible) return;
        if (this.socialProofObserver) this.socialProofObserver.disconnect();
        this.scheduleSocialProof();
      }

      scheduleSocialProof() {
        if (this.socialProofHasShown) return;
        if (this.socialProofDelayTimeout) window.clearTimeout(this.socialProofDelayTimeout);
        this.socialProofDelayTimeout = window.setTimeout(() => {
          this.showSocialProof();
        }, 2500);
      }

      resolveSocialProofCount() {
        const stored = this.getStoredSocialProofCount();
        if (stored !== null) return stored;
        const generated = this.generateSocialProofCount();
        this.persistSocialProofCount(generated);
        return generated;
      }

      getStoredSocialProofCount() {
        try {
          const raw = window.localStorage.getItem(this.socialProofStorageKey);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed.value !== 'number' || typeof parsed.expiresAt !== 'number') return null;
          if (Date.now() > parsed.expiresAt) {
            window.localStorage.removeItem(this.socialProofStorageKey);
            return null;
          }
          return parsed.value;
        } catch (error) {
          return null;
        }
      }

      persistSocialProofCount(value) {
        try {
          const payload = JSON.stringify({ value, expiresAt: Date.now() + this.socialProofTTL });
          window.localStorage.setItem(this.socialProofStorageKey, payload);
        } catch (error) {}
      }

      generateSocialProofCount() {
        return Math.floor(Math.random() * 101) + 50;
      }

      updateSocialProofCount() {
        if (!this.socialProofCountElement) return;
        this.socialProofCountElement.textContent = `${this.socialProofCount}+`;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
